import { Injectable } from '@angular/core';
import { BuildingTypesService } from '@app/shared/services/api/building-types.services';
import { EfficiencyAnalysisApiService } from '@app/shared/services/api/efficiency-analysis-api.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { BehaviorSubject, Observable, combineLatest, forkJoin } from 'rxjs';
import { distinctUntilChanged, filter, first, switchMap, tap, map } from 'rxjs/operators';
import { ComparisonValues } from '../models/comparison-values';
import { MapStoreMunicipality } from '@app/shared/models/municipality/Municipality';
import { IBuildingEfficiencyData } from '../models/buildings/building-efficiency-data';
import { BuildingTypes } from '@app/shared/models/building-types';
import { BuildingEfficiencyService } from '@app/shared/services/api/building-efficiency.service';

@Injectable({
  providedIn: 'root',
})
export class EfficiencyAnalysisDatastoreService {
  private buildingEfficiencyAnalysisStore: BehaviorSubject<IBuildingEfficiencyData[]> = new BehaviorSubject<IBuildingEfficiencyData[]>([]);
  private buildingTypesStore: BehaviorSubject<BuildingTypes[]> = new BehaviorSubject<BuildingTypes[]>([]);
  private comparisonValuesStore: BehaviorSubject<ComparisonValues[]> = new BehaviorSubject<ComparisonValues[]>([]);
  public buildingEfficiencyAnalysis$: Observable<IBuildingEfficiencyData[]> = this.buildingEfficiencyAnalysisStore.asObservable();
  public buildingTypes$: Observable<BuildingTypes[]> = this.buildingTypesStore.asObservable();
  public comparisonValues$: Observable<ComparisonValues[]> = this.comparisonValuesStore.asObservable();

  constructor(
    private buildingTypesService: BuildingTypesService,
    private efficiencyAnalysisApiService: EfficiencyAnalysisApiService,
    private userMunicipalitiesService: UserMunicipalitiesService,
    private buildingEfficiencyService: BuildingEfficiencyService
  ) {
    this.init();
  }

  private init(): void {
    forkJoin([this.efficiencyAnalysisApiService.getComparisonValues(), this.buildingTypesService.getAll()])
      .pipe(first())
      .subscribe(([comparisonValues, buildingTypes]: [ComparisonValues[], BuildingTypes[]]) => {
        this.comparisonValuesStore.next(comparisonValues);
        this.buildingTypesStore.next(buildingTypes);
      });
  }

  private load(): Observable<IBuildingEfficiencyData[]> {
    return this.userMunicipalitiesService.currentMunicipality$.pipe(
      filter((municipality: MapStoreMunicipality) => !!municipality?.guid && !!municipality?.key),
      distinctUntilChanged(),
      switchMap((municipality: MapStoreMunicipality) =>
        forkJoin([
          this.buildingEfficiencyService.getBuildingsbyMunicipalityKey(municipality.key),
          this.buildingEfficiencyService.getDeactivatedBuildingsWithEA(municipality.key),
        ]).pipe(map(([building, deactivatedBuilding]: [IBuildingEfficiencyData[], IBuildingEfficiencyData[]]) => [...building, ...deactivatedBuilding]))
      ),
      tap((mergedBuildingData: IBuildingEfficiencyData[]) => {
        this.buildingEfficiencyAnalysisStore.next(mergedBuildingData);
      })
    );
  }

  public loadBuildingEfficiencyAnalysis(): Observable<[IBuildingEfficiencyData[], ComparisonValues[], BuildingTypes[]]> {
    return combineLatest([
      this.load(),
      this.efficiencyAnalysisApiService.getComparisonValues().pipe(tap((values: ComparisonValues[]) => this.comparisonValuesStore.next(values))),
      this.buildingTypesService.getAll().pipe(tap((types: BuildingTypes[]) => this.buildingTypesStore.next(types))),
    ]);
  }

  public deleteEfficiencyAnalysis(buildingId: string, municipalityKey: string): Observable<null> {
    return this.efficiencyAnalysisApiService.deleteEfficiencyAnalysisByBuildngId(buildingId, municipalityKey);
  }
}
